/**
 * EntityArrayAutocompleteField Component (issue #775)
 *
 * Filterable, multi-select entity picker: an `EntityAutocompleteField` for
 * adding one entity at a time (already-selected entities drop out of its
 * option list) plus a removable chip list of the current selection below —
 * the multi-value counterpart to the single-select `EntityAutocompleteField`.
 * Reuses the chip markup/classes from `ArrayInputField` (the free-text
 * array input this replaces for entity-id arrays) rather than duplicating
 * that CSS.
 *
 * Unlike `ArrayInputField`, the "add" affordance is a filterable catalog
 * pick rather than arbitrary typed text, so the resulting array can only
 * ever contain ids the caller's `options` catalog actually knows about.
 */

import { EntityAutocompleteField, type EntityAutocompleteOption } from './EntityAutocompleteField';
import type { FieldError } from 'react-hook-form';
import '../shared/ArrayInputField.css';

export type EntityArrayAutocompleteOption = EntityAutocompleteOption;

export interface EntityArrayAutocompleteFieldProps {
  /** Field name — used for the add-input's id/name and error association */
  name: string;
  /** Field label */
  label?: string;
  /** Entities the user may add */
  options: EntityArrayAutocompleteOption[];
  /** Currently selected entity ids */
  value: string[];
  /** Called with the full updated list of selected entity ids */
  onChange: (entityIds: string[]) => void;
  /** Placeholder text for the add input */
  placeholder?: string;
  /** Is field disabled */
  disabled?: boolean;
  /** Helper text */
  helperText?: string;
  /** Validation error from React Hook Form */
  error?: FieldError;
  /** Message shown when the filter text matches no entity */
  emptyMessage?: string;
  /** Accessible label for each chip's remove button */
  removeAriaLabel?: string;
}

/**
 * @example
 * ```tsx
 * <EntityArrayAutocompleteField
 *   name="memberIds"
 *   label="Team Member IDs"
 *   options={users.map((u) => ({ value: u.userId, label: `${u.name} (${u.email})` }))}
 *   value={values.memberIds || []}
 *   onChange={(ids) => setValue('memberIds', ids)}
 *   placeholder="Search users…"
 *   removeAriaLabel="Remove member"
 * />
 * ```
 */
export function EntityArrayAutocompleteField({
  name,
  label,
  options,
  value,
  onChange,
  placeholder = 'Search…',
  disabled = false,
  helperText,
  error,
  emptyMessage = 'No matches found',
  removeAriaLabel = 'Remove item',
}: EntityArrayAutocompleteFieldProps): JSX.Element {
  const selectedEntries = value.map(
    (id) => options.find((option) => option.value === id) ?? { value: id, label: id }
  );
  const availableOptions = options.filter((option) => !value.includes(option.value));

  const handleAdd = (entityId: string): void => {
    if (!entityId || value.includes(entityId)) return;
    onChange([...value, entityId]);
  };

  const handleRemove = (entityId: string): void => {
    onChange(value.filter((id) => id !== entityId));
  };

  return (
    <div className="array-field">
      <EntityAutocompleteField
        name={name}
        label={label}
        options={availableOptions}
        value=""
        onChange={handleAdd}
        placeholder={placeholder}
        disabled={disabled}
        helperText={helperText}
        error={error}
        emptyMessage={emptyMessage}
      />
      {selectedEntries.length > 0 && (
        <div className="chip-list">
          {selectedEntries.map((entry) => (
            <div key={entry.value} className="chip">
              <span className="chip-text">{entry.label}</span>
              <button
                type="button"
                className="chip-remove"
                onClick={() => handleRemove(entry.value)}
                disabled={disabled}
                aria-label={removeAriaLabel}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
